/**
 * 设计 tokens — 跨端共享的真理来源。
 *
 *   - H5（apps/h5）用 react-vant 主题 + CSS 变量
 *   - 小程序（apps/miniprogram）用 vant-weapp 同名变量
 *   - 管理后台（apps/admin）用 AntD ConfigProvider 的 token 系统对齐主色
 *
 * 调性目标：奢侈品聚合（参考 Net-A-Porter / 24S / Lyst）。
 * 配色摒弃饱和红，改用：
 *   - 深酒红 #6d2839（主色 / brand accent）
 *   - 香槟金 #b8956b（次色 / metallic accent）
 *   - 炭黑 #1a1a1a（正文）
 *   - 米白 #faf8f5（背景）
 *   - 暖灰 #e8e2dc（边框）
 */

export const colors = {
  // Brand
  primary: "#6d2839", // 深酒红
  primaryDark: "#4a1c2a",
  primaryLight: "#f5e9eb",
  accentGold: "#b8956b", // 香槟金
  accentGoldDark: "#8a6e4f",
  accentGoldLight: "#f0e7d8",

  // Semantic
  success: "#2e5d3a",
  warning: "#a87830",
  danger: "#a83d4a",
  info: "#2e5d8a",

  // Text
  textPrimary: "#1a1a1a",
  textSecondary: "#4a4a4a",
  textMuted: "#8a8580",
  textInverse: "#ffffff",

  // Surface
  bg: "#faf8f5", // 米白
  card: "#ffffff",
  border: "#e8e2dc", // 暖灰
  borderStrong: "#d1c9bf",

  // Trust badges
  trustOfficial: "#6d2839",
  trustOfficialBg: "#f5e9eb",
  trustHigh: "#2e5d3a",
  trustHighBg: "#e7eee9",
  trustMedium: "#a87830",
  trustMediumBg: "#f3ebd9",
  trustUnknown: "#4a4a4a",
  trustUnknownBg: "#ececec",
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
} as const;

export const fontSize = {
  xs: 11,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 20,
  xxl: 26,
} as const;

export const shadow = {
  card: "0 1px 2px rgba(20, 10, 5, 0.04)",
  raised: "0 4px 16px rgba(20, 10, 5, 0.08)",
  bottomBar: "0 -1px 4px rgba(20, 10, 5, 0.04)",
} as const;

/**
 * react-vant ConfigProvider 主题变量。
 */
export const vantThemeVars = {
  "--rv-primary-color": colors.primary,
  "--rv-success-color": colors.success,
  "--rv-warning-color": colors.warning,
  "--rv-danger-color": colors.danger,
  "--rv-text-color": colors.textPrimary,
  "--rv-text-color-2": colors.textSecondary,
  "--rv-text-color-3": colors.textMuted,
  "--rv-background-color": colors.bg,
  "--rv-background-color-light": colors.card,
  "--rv-border-color": colors.border,
  "--rv-button-primary-background-color": colors.primary,
  "--rv-button-primary-border-color": colors.primary,
  "--rv-tabbar-item-active-color": colors.primary,
  "--rv-nav-bar-icon-color": colors.textPrimary,
  "--rv-nav-bar-title-text-color": colors.textPrimary,
  "--rv-tag-primary-color": colors.primary,
  "--rv-cell-active-color": colors.bg,
} as const;
